import { Component, ElementRef, HostListener, Input, OnInit, forwardRef } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { GooglePlacesService } from '../../services/google-places/google-places.service';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-multi-select-locations',
  templateUrl: './multi-select-locations.component.html',
  styleUrls: ['./multi-select-locations.component.scss'],
  providers: [{
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => MultiSelectLocationsComponent),
    multi: true
  }]
})
export class MultiSelectLocationsComponent implements OnInit, ControlValueAccessor {

  @Input() dropdownItems: any[] = [];
  @Input() dropdownOnTop: boolean = false;
  @Input() placeHolder: string = "-- select countries --";
  @Input() googleLocations: boolean = false;

  dropdownVisible: boolean = false;
  selectedItems: any[] = [];
  filteredItems: any[] = [];
  searchTerm: string = '';

  private searchSubject = new Subject<string>();

  constructor(
    private elementRef: ElementRef,
    private _googlePlacesService: GooglePlacesService
  ) {}

  onChange: any = () => {};
  onTouched: any = () => {};

  ngOnInit() {
    this.filteredItems = [...this.dropdownItems];

    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged()
    ).subscribe(input => {
      this.searchTerm = input;

      if (this.googleLocations) {
        if (input && input.length > 2) {
          this.fetchGoogleLocations(input);
        } else {
          this.filteredItems = [];
        }
      } else {
        this.filteredItems = this.dropdownItems
          .filter(item => item.name.toLowerCase().includes(this.searchTerm.toLowerCase()))
          .sort(this.sortItems.bind(this));
      }
    });
  }

  writeValue(value: string[]): void {
    if (this.googleLocations) {
      this.selectedItems = value.map(val => ({
        name: val,
        code3: val
      }));
    } else {
      this.selectedItems = this.dropdownItems.filter(item => value.includes(item.name));
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {}

  onSearch(event: any) {
    const input = event.target.value;
    this.searchSubject.next(input);
  }

  private async fetchGoogleLocations(input: string) {
    try {
      const predictions = await this._googlePlacesService.getPlacePredictions(input, [], null);
      this.filteredItems = predictions.map(a => ({
        ...a,
        name: a.description,
        code3: a.description
      }));
    } catch {
      this.dropdownItems = [];
    }
  }

  isSelected(item: any): boolean {
    return this.selectedItems.some(selectedItem => selectedItem.name === item.name);
  }

  toggleSelection(item: any) {
    if (this.isSelected(item)) {
      this.selectedItems = this.selectedItems.filter(i => i.name !== item.name);
    } else {
      this.selectedItems.push(item);
    }
    this.filteredItems.sort(this.sortItems.bind(this));
    this.onChange(this.selectedItems.map(i => i.name));
  }

  removeItem(item: any) {
    this.selectedItems = this.selectedItems.filter(i => i.name !== item.name);
    this.filteredItems.sort(this.sortItems.bind(this));
    this.onChange(this.selectedItems.map(i => i.name));
  }

  toggleDropdown(event: MouseEvent) {
    this.dropdownVisible = !this.dropdownVisible;
    event.stopPropagation();
  }

  private sortItems(a: any, b: any): number {
    return this.isSelected(a) === this.isSelected(b) ? 0 : this.isSelected(a) ? -1 : 1;
  }

  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent) {
    if (!this.elementRef.nativeElement.contains(event.target as HTMLElement)) {
      this.dropdownVisible = false;
    }
  }
}